import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Globe, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { linkedInApi } from "@/services/linkedin-api";
import { formatDateTime } from "@/utils/dateUtils";

const QUANTIDADES_SUGERIDAS = [50, 100, 250, 500];

interface Props {
  onIniciar: (limite: number) => void;
  iniciando: boolean;
}

/**
 * Dialog da busca automática: escolhe quantos perfis entram no lote e mostra
 * quem entra primeiro (quem está há mais tempo sem ser buscado).
 */
export function LinkedInScrapeDialog({ onIniciar, iniciando }: Props) {
  const [aberto, setAberto] = useState(false);
  const [quantidade, setQuantidade] = useState("100");

  const previewQuery = useQuery({
    queryKey: ["linkedin-scrape-preview"],
    queryFn: () => linkedInApi.scrapePreview(),
    enabled: aberto,
  });
  const preview = previewQuery.data;

  const quantidadeNumero = Number(quantidade);
  const quantidadeValida =
    Number.isInteger(quantidadeNumero) && quantidadeNumero > 0;
  const totalDoLote = preview
    ? Math.min(quantidadeNumero || 0, preview.elegiveis)
    : quantidadeNumero;

  return (
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5" disabled={iniciando}>
          {iniciando ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Globe className="h-4 w-4" />
          )}
          Busca automática
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Busca automática de perfis</DialogTitle>
          <DialogDescription>
            Busca os perfis públicos do LinkedIn e cria um lote com os dados
            encontrados. Cada perfil passa pela mesma revisão dos uploads antes
            de valer no cadastro.
          </DialogDescription>
        </DialogHeader>

        {previewQuery.isPending ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="font-medium">
                {preview?.elegiveis ?? 0} freelancer
                {preview?.elegiveis === 1 ? "" : "s"} na fila
              </div>
              <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                <li>
                  Entram primeiro os que estão há mais tempo sem busca (quem
                  nunca foi buscado vem antes de todos).
                </li>
                {(preview?.nunca_buscados ?? 0) > 0 && (
                  <li>{preview?.nunca_buscados} nunca foram buscados.</li>
                )}
                {preview?.busca_mais_antiga && (
                  <li>
                    Busca mais antiga da fila: {formatDateTime(preview.busca_mais_antiga)}.
                  </li>
                )}
                {(preview?.ocupados ?? 0) > 0 && (
                  <li>
                    {preview?.ocupados} ficam de fora por já terem processamento
                    em andamento ou revisão pendente.
                  </li>
                )}
                {(preview?.sem_url ?? 0) > 0 && (
                  <li>
                    {preview?.sem_url} não têm link de perfil cadastrado e não
                    podem ser buscados.
                  </li>
                )}
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantidade-lote">Quantos processar agora</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="quantidade-lote"
                  type="number"
                  min={1}
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value)}
                  className="w-28"
                />
                {QUANTIDADES_SUGERIDAS.map((sugestao) => (
                  <Button
                    key={sugestao}
                    type="button"
                    variant={quantidade === String(sugestao) ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setQuantidade(String(sugestao))}
                  >
                    {sugestao}
                  </Button>
                ))}
                {preview && (
                  <Button
                    type="button"
                    variant={
                      quantidade === String(preview.elegiveis) ? "secondary" : "ghost"
                    }
                    size="sm"
                    onClick={() => setQuantidade(String(preview.elegiveis))}
                  >
                    Todos ({preview.elegiveis})
                  </Button>
                )}
              </div>
              {preview && quantidadeValida && (
                <p className="text-xs text-muted-foreground">
                  O lote vai ter {totalDoLote} perfil
                  {totalDoLote === 1 ? "" : "s"}.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setAberto(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!quantidadeValida || iniciando || !preview?.elegiveis}
            onClick={() => {
              onIniciar(quantidadeNumero);
              setAberto(false);
            }}
          >
            Iniciar busca
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
